import {select, take, fork, put} from 'redux-saga/effects';
import {selectSearchQueryParams} from '@computerrock/formation-router/sagas';
import fetchRequest from '../../application/sagas/fetchRequest';
import * as serviceCaseActionTypes from '../serviceCaseActionTypes';
import savScreenTabs from '../../service-assignments-vehicle/savScreenTabs';
import sasdScreenTabs from '../../service-assignments-substitute-driver/sasdScreenTabs';
import samcScreenTabs from '../../service-assignments-medical-clearance/samcScreenTabs';
import serviceCaseScreenTabs from '../serviceCaseScreenTabs';
import vehicleTypesMapping from '../vehicleTypesMapping';
import {getManufacturerIdByName, getModelIdByName} from '../useVehicleInfo';
import samsScreenTabs from '../../service-assignments-medicine-shipment/samsScreenTabs';
import sadehScreenTabs from '../../service-assignments-death-event-help/sadehScreenTabs';
import modalIds from '../../modalIds';

const loadServiceCaseVehicleInfo = function* loadServiceCaseVehicleInfo({payload}) {
    const {serviceManager} = yield select(state => state.application);
    const silverDATService = serviceManager.loadService('silverDATService');
    const {vehicleInfos, serviceCases} = yield select(state => state.serviceCases);

    const {activeTab, modal} = yield* selectSearchQueryParams();
    if (![
        savScreenTabs.VEHICLE,
        sasdScreenTabs.TRIP_DATA,
        sadehScreenTabs.TRIP_DATA,
        samcScreenTabs.TRIP_DATA,
        samsScreenTabs.TRIP_DATA,
        serviceCaseScreenTabs.BASIC_DATA,
    ].includes(activeTab)) return;
    // on the SC basic data tab, load vehicle info only if the user opens a vehicle modal
    if (activeTab === serviceCaseScreenTabs.BASIC_DATA && modal !== modalIds.VEHICLE_DATA) return;

    const {match} = payload;
    const {serviceCaseId} = match.params;
    const serviceCase = serviceCases[serviceCaseId];
    if (!serviceCase) return;

    // CAR is default vehicle type in create vehicle form
    const vehicleType = serviceCase.vehicle?.type
        ? vehicleTypesMapping[serviceCase.vehicle.type]
        : vehicleTypesMapping.CAR;

    // if manufacturers already exist, do not load them again
    if (vehicleInfos[vehicleType]) return;

    yield fork(
        fetchRequest,
        serviceCaseActionTypes.FETCH_VEHICLE_MANUFACTURERS_REQUEST,
        silverDATService.getVehicleManufacturers, {
            vehicleType,
        },
    );

    const responseAction = yield take([
        serviceCaseActionTypes.FETCH_VEHICLE_MANUFACTURERS_REQUEST_SUCCEEDED,
        serviceCaseActionTypes.FETCH_VEHICLE_MANUFACTURERS_REQUEST_FAILED,
    ]);

    if (!responseAction.error) {
        const {response} = responseAction.payload;
        const {vehicleManufacturers} = response;

        yield put({
            type: serviceCaseActionTypes.STORE_VEHICLE_MANUFACTURERS,
            payload: {vehicleManufacturers, vehicleType},
        });

        // get models if manufacturer exists
        if (serviceCase.vehicle?.type && serviceCase.vehicle?.manufacturer) {
            const manufacturerId = getManufacturerIdByName(
                {
                    [vehicleType]: vehicleManufacturers,
                },
                {type: serviceCase.vehicle.type, manufacturer: serviceCase.vehicle.manufacturer},
            );

            if (manufacturerId) {
                yield fork(
                    fetchRequest,
                    serviceCaseActionTypes.FETCH_VEHICLE_MODELS_REQUEST,
                    silverDATService.getVehicleModels, {
                        vehicleType,
                        manufacturer: manufacturerId,
                    },
                );

                const responseAction = yield take([
                    serviceCaseActionTypes.FETCH_VEHICLE_MODELS_REQUEST_SUCCEEDED,
                    serviceCaseActionTypes.FETCH_VEHICLE_MODELS_REQUEST_FAILED,
                ]);

                if (!responseAction.error) {
                    const {response} = responseAction.payload;
                    const {vehicleModels} = response;

                    yield put({
                        type: serviceCaseActionTypes.STORE_VEHICLE_MODELS,
                        payload: {vehicleModels, vehicleType, manufacturer: manufacturerId},
                    });

                    // get variants if model exists
                    if (serviceCase.vehicle?.model) {
                        const modelId = getModelIdByName(
                            {
                                [vehicleType]: {
                                    [manufacturerId]: {
                                        name: serviceCase.vehicle.manufacturer,
                                        models: vehicleModels,
                                    },
                                },
                            },
                            {
                                type: serviceCase.vehicle.type,
                                manufacturer: serviceCase.vehicle.manufacturer,
                                model: serviceCase.vehicle.model,
                            },
                        );
                        if (modelId) {
                            yield fork(
                                fetchRequest,
                                serviceCaseActionTypes.FETCH_VEHICLE_VARIANTS_REQUEST,
                                silverDATService.getVehicleVariants, {
                                    vehicleType,
                                    manufacturer: manufacturerId,
                                    baseModel: modelId,
                                },
                            );

                            const responseAction = yield take([
                                serviceCaseActionTypes.FETCH_VEHICLE_VARIANTS_REQUEST_SUCCEEDED,
                                serviceCaseActionTypes.FETCH_VEHICLE_VARIANTS_REQUEST_FAILED,
                            ]);

                            if (!responseAction.error) {
                                const {response} = responseAction.payload;
                                const {vehicleVariants} = response;

                                yield put({
                                    type: serviceCaseActionTypes.STORE_VEHICLE_VARIANTS,
                                    payload: {
                                        vehicleVariants,
                                        vehicleType,
                                        manufacturer: manufacturerId,
                                        baseModel: modelId,
                                    },
                                });
                            }
                        }
                    }
                }
            }
        }
    }
};

export default loadServiceCaseVehicleInfo;
